import { requestClient } from '#/api'
import { defineApi } from '#/api/define-api'

export type AgentCurrency = 'EUR' | 'USD'

export interface AgentWalletBalance {
  availableBalance?: number
  balance?: number
  code?: string
  name?: string
  taxHoldBalance?: number
  walletId?: string
}

export interface AgentBalanceDetail {
  defaultCurrency?: AgentCurrency
  EUR?: AgentWalletBalance
  USD?: AgentWalletBalance
}

export interface AgentAlarmThresholds {
  EUR: number[]
  USD: number[]
}

interface AgentAlarmConfig {
  atOpenIds?: string[]
  thresholds?: Partial<AgentAlarmThresholds>
  webhook?: string
}

interface AgentListApiItem {
  accessMethod: string
  alarmConfig?: AgentAlarmConfig
  balanceDetail?: AgentBalanceDetail
  balanceTime?: string
  cnName: string
  enName: string
  id: number
  isAlarm: number
  rate: number
  rateStatus: number
  updatedBy?: string
  updatedTime?: string
}

interface AgentListApiResponse {
  list?: AgentListApiItem[]
  total?: number
}

export interface AgentListItem {
  accessMethod: string
  atOpenIds: string[]
  balanceDetail?: AgentBalanceDetail
  balanceTime?: string
  cnName: string
  enName: string
  id: number
  isAlarm: boolean
  rate: number
  rateStatus: string
  thresholds: AgentAlarmThresholds
  updatedBy?: string
  updatedTime?: string
  webhook?: string
}

export interface UpdateAgentParams {
  accessMethod: string
  atOpenIds: string[]
  cnName: string
  enName: string
  id?: number
  isAlarm: boolean
  rate: number
  rateStatus: string
  thresholds: AgentAlarmThresholds
  webhook?: string
}

interface RbcUserListItem {
  email?: string
  employeeNo?: string
  feishuOpenId: string
  nickName: string
  userId?: number | string
  userName?: string
}

interface RbcUserListResponse {
  list?: RbcUserListItem[]
}

interface RbcUserOption {
  email: string
  employeeNo?: string
  feishuOpenId: string
  label: string
  nickName: string
  userId?: string
  userName: string
  value: string
}

export const agentApi = defineApi('operation', ({ prefix }) => {
  /**
   * 查询代理列表。
   * POST /adsAgency/query
   */
  function getAgentList() {
    return requestClient
      .post<AgentListApiResponse>(prefix('adsAgency/query'), {})
      .then((data) => ({
        items: (data.list ?? []).map((item) => ({
          accessMethod: item.accessMethod,
          atOpenIds: item.alarmConfig?.atOpenIds ?? [],
          balanceDetail: item.balanceDetail,
          balanceTime: item.balanceTime,
          cnName: item.cnName,
          enName: item.enName,
          id: item.id,
          isAlarm: item.isAlarm === 1,
          rate: item.rate,
          rateStatus: String(item.rateStatus),
          thresholds: {
            EUR: item.alarmConfig?.thresholds?.EUR ?? [],
            USD: item.alarmConfig?.thresholds?.USD ?? [],
          },
          updatedBy: item.updatedBy,
          updatedTime: item.updatedTime,
          webhook: item.alarmConfig?.webhook,
        })),
        total: data.total ?? 0,
      }))
  }

  /**
   * 新增代理。
   * POST /adsAgency/create
   */
  function createAgent(params: UpdateAgentParams) {
    return requestClient.post(prefix('adsAgency/create'), {
      accessMethod: params.accessMethod,
      alarmConfig: {
        atOpenIds: params.atOpenIds,
        thresholds: params.thresholds,
        webhook: params.webhook,
      },
      cnName: params.cnName,
      enName: params.enName,
      isAlarm: params.isAlarm ? 1 : 2,
      rate: Number(params.rate),
      rateStatus: Number(params.rateStatus),
    })
  }

  /**
   * 更新代理。
   * POST /adsAgency/update
   */
  function updateAgent(params: UpdateAgentParams) {
    return requestClient.post(prefix('adsAgency/update'), {
      accessMethod: params.accessMethod,
      alarmConfig: {
        atOpenIds: params.atOpenIds,
        thresholds: params.thresholds,
        webhook: params.webhook,
      },
      cnName: params.cnName,
      enName: params.enName,
      id: params.id,
      isAlarm: params.isAlarm ? 1 : 2,
      rate: Number(params.rate),
      rateStatus: Number(params.rateStatus),
    })
  }

  /**
   * 查询 RBC 用户选项。
   * GET /rbc/getUserList
   */
  function getRbcUserList() {
    return requestClient
      .get<RbcUserListResponse>(prefix('rbc/getUserList'))
      .then((data) =>
        (data.list ?? []).map(
          (item): RbcUserOption => ({
            email: item.email ?? '',
            employeeNo: item.employeeNo,
            feishuOpenId: item.feishuOpenId,
            label: `${item.nickName}${item.employeeNo ? ` (${item.employeeNo})` : ''}`,
            nickName: item.nickName,
            userId:
              item.userId === undefined || item.userId === null
                ? ''
                : String(item.userId),
            userName: item.userName ?? '',
            value: item.feishuOpenId,
          }),
        ),
      )
  }

  return {
    createAgent,
    getAgentList,
    getRbcUserList,
    updateAgent,
  }
})
